/* eslint-disable react/no-unknown-property */

import { ComponentResource } from '@/api/serverApi'
import { useSelector } from 'react-redux'
import { useMemo, useState } from 'react'
import { Color, Mesh, MeshPhysicalMaterial, PMREMGenerator } from 'three'
import { RootState } from '@/stores'
import { ThreeColors } from '@/helpers/threeColors'
import { useThree } from '@react-three/fiber'
import { useGLTF } from '@react-three/drei'
import AnchorModel from './AnchorModel'

type PropTypes = {
  component: ComponentResource
}

const CastInChannelModel = ({ component }: PropTypes) => {
  const three = useThree()

  const scene = useGLTF(component.model)

  const productVariants = useSelector((state: RootState) => state.products.productVariants)

  const selectedCameraControlType = useSelector((state: RootState) => state.selectedCameraControlType.option)

  const productVariant = productVariants?.find(p => p.id === component.product_variant_id)

  const isSelected = useSelector((state: RootState) => state.activeDesign.selectedComponentId === component.id)

  const [isHovered, setIsHovered] = useState(false)

  const scale = useMemo<[number, number, number]>(() => {
    const channelWidthDivider = 17
    const channelDepthDivider = 18
    const channelWidthOffset = 0

    const channelDepth = productVariant?.cast_in_anchor_channel?.depth ?? 1
    const channelWidth = productVariant?.cast_in_anchor_channel?.width ?? 1
    const channelLength = component?.cast_in_anchor_channel?.custom_length ?? component?.cast_in_anchor_channel?.length ?? 1

    const halfChannelWidth = channelWidth / 2

    const xScale = ((halfChannelWidth / channelWidthDivider) - channelWidthOffset)
    const yScale = channelLength
    const zScale = channelDepth / channelDepthDivider

    return [
      xScale,
      yScale,
      zScale,
    ]
  }, [component, productVariant])

  const offset = useMemo<[number, number, number]>(() => {
    const channelDepth = productVariant?.cast_in_anchor_channel?.depth ?? 1
    const channelLength = component?.cast_in_anchor_channel?.custom_length ?? component?.cast_in_anchor_channel?.length ?? 1

    const halfChannelDepth = channelDepth / 2
    const halfChannelLength = channelLength / 2

    const xOffset = 0
    const yOffset = -halfChannelDepth
    const zOffset = -halfChannelLength

    return [
      zOffset,
      yOffset,
      xOffset,
    ]
  }, [component, productVariant])

  const anchors = useMemo(() => {
    const anchorSpacings = component?.cast_in_anchor_channel?.anchor_spacings

    if (!anchorSpacings) {
      return []
    }

    return anchorSpacings.map((spacing, index) => {
      return (
        <AnchorModel
          key={`anchor-${index}`}
          anchorSpacing={spacing}
          channel={component}
        />
      )
    })
  }, [component])

  const envMap = useMemo(() => {
    const generator = new PMREMGenerator(three.gl).fromScene(three.scene)
    return generator.texture
  }, [three])

  const channelModel = useMemo(() => {
    return Object.entries(scene.nodes).map(([key, value]) => {
      return (
        <mesh
          key={key}
          receiveShadow
          geometry={(value as Mesh).geometry}
          material={new MeshPhysicalMaterial({
            ior: 1.52,
            specularIntensity: 0.5,
            roughness: 0.3,
            metalness: 0.6,
            envMap: envMap,
            color: isSelected
              ? ThreeColors.selectedColor
              : isHovered
                ? new Color(0.05, 0.05, 0.05)
                : new Color(0.15, 0.15, 0.15),
          })}
        />
      )
    })
  }, [scene, isHovered, isSelected, envMap])

  return (

    <group
      onPointerEnter={(e) => {
        e.stopPropagation()

        if (selectedCameraControlType !== 'cursor') {
          return
        }

        setIsHovered(true)
      }}
      onPointerLeave={(e) => {
        e.stopPropagation()

        if (selectedCameraControlType !== 'cursor') {
          return
        }

        setIsHovered(false)
      }}
    >
      <group
        scale={scale}
        rotation={[Math.PI / 2, Math.PI, Math.PI / 2]}
        position={offset}
      >
        {channelModel}
      </group>
      <group
        position={offset}
      >
        {anchors}
      </group>
    </group>
  )
}

export default CastInChannelModel
